/**
 * Packages & Services builder — shared const option tables + presets.
 * Extracted verbatim from the former AgencyServicesPage monolith
 * (Task 150-A). No behavior change.
 */

import type {
  PackageBillingInterval,
  PackageServiceDeliverable,
  PricingModel,
  ServiceBillingInterval,
  ServiceCadence,
  SystemCapabilityKey,
} from '../../api/types';

// Commercial service presets — what the agency owner actually thinks
// in. Each preset suggests a sensible default capability set; the
// owner can edit before saving. `custom` skips the suggestion.
export interface ServicePreset {
  readonly id: string;
  readonly name: string;
  readonly suggestedDescription: string;
  readonly suggestedCapabilities: ReadonlyArray<SystemCapabilityKey>;
  readonly suggestedPrimary: SystemCapabilityKey;
}

const INFLUENCER_SERVICE_PRESETS_ENABLED = false;

const ALL_SERVICE_PRESETS: ReadonlyArray<ServicePreset> = [
  {
    id: 'social_content',
    name: 'إدارة السوشال ميديا والمحتوى',
    suggestedDescription:
      'إدارة شاملة لحسابات السوشال ميديا، تخطيط المحتوى، النشر، والتقارير الشهرية.',
    suggestedCapabilities: ['content', 'publishing', 'creative', 'reports'],
    suggestedPrimary: 'content',
  },
  {
    id: 'paid_media',
    name: 'إدارة الإعلانات المدفوعة',
    suggestedDescription:
      'إدارة حملات Meta و Google و TikTok و Snapchat، البناء، التحسين، والتقارير.',
    suggestedCapabilities: ['paid_media', 'creative', 'reports'],
    suggestedPrimary: 'paid_media',
  },
  {
    id: 'influencers',
    name: 'إدارة حملات المؤثرين',
    suggestedDescription: 'اختيار المؤثرين، تنسيق الحملات، إدارة المحتوى المرتبط، وقياس الأثر.',
    suggestedCapabilities: ['influencers', 'content', 'reports'],
    suggestedPrimary: 'influencers',
  },
  {
    id: 'creative_production',
    name: 'إنتاج المحتوى والإبداع',
    suggestedDescription: 'تصميم، تصوير، إنتاج فيديو، وتطوير مكتبة الإبداع.',
    suggestedCapabilities: ['creative', 'content'],
    suggestedPrimary: 'creative',
  },
  {
    id: 'custom',
    name: 'خدمة مخصصة',
    suggestedDescription: '',
    suggestedCapabilities: ['custom'],
    suggestedPrimary: 'custom',
  },
];

export const SERVICE_PRESETS: ReadonlyArray<ServicePreset> = ALL_SERVICE_PRESETS.filter(
  (preset) => INFLUENCER_SERVICE_PRESETS_ENABLED || preset.id !== 'influencers',
);

export const SERVICE_BILLING_INTERVALS: ReadonlyArray<{
  readonly value: ServiceBillingInterval | '';
  readonly label: string;
}> = [
  { value: '', label: 'بدون' },
  { value: 'one_time', label: 'مرّة واحدة' },
  { value: 'monthly', label: 'شهري' },
  { value: 'quarterly', label: 'ربع سنوي' },
  { value: 'yearly', label: 'سنوي' },
  { value: 'custom', label: 'مخصّص' },
];

export const PACKAGE_BILLING_INTERVALS: ReadonlyArray<{
  readonly value: PackageBillingInterval | '';
  readonly label: string;
}> = [
  { value: '', label: 'بدون' },
  { value: 'monthly', label: 'شهري' },
  { value: 'quarterly', label: 'ربع سنوي' },
  { value: 'yearly', label: 'سنوي' },
  { value: 'custom', label: 'مخصّص' },
];

export const PRICING_MODELS: ReadonlyArray<{
  readonly value: PricingModel | '';
  readonly label: string;
}> = [
  { value: '', label: 'بدون نموذج محدّد' },
  { value: 'included', label: 'مضمّن في الباقة' },
  { value: 'fixed', label: 'سعر ثابت' },
  { value: 'percentage_of_spend', label: 'نسبة من الإنفاق' },
  { value: 'fixed_plus_percentage', label: 'ثابت + نسبة' },
  { value: 'per_campaign', label: 'لكل حملة' },
  { value: 'custom', label: 'مخصّص' },
];

export const SERVICE_CADENCES: ReadonlyArray<{
  readonly value: ServiceCadence | '';
  readonly label: string;
}> = [
  { value: '', label: 'بدون' },
  { value: 'one_time', label: 'مرّة واحدة' },
  { value: 'weekly', label: 'أسبوعي' },
  { value: 'monthly', label: 'شهري' },
  { value: 'quarterly', label: 'ربع سنوي' },
  { value: 'per_campaign', label: 'لكل حملة' },
  { value: 'custom', label: 'مخصّص' },
];

export const DELIVERABLE_TYPE_OPTIONS: ReadonlyArray<{
  readonly value: PackageServiceDeliverable['type'];
  readonly label: string;
}> = [
  { value: 'design', label: 'تصميم' },
  { value: 'post', label: 'منشور' },
  { value: 'reel', label: 'ريل' },
  { value: 'story', label: 'ستوري' },
  { value: 'video', label: 'فيديو' },
  { value: 'campaign', label: 'حملة' },
  { value: 'report', label: 'تقرير' },
  { value: 'meeting', label: 'اجتماع' },
  { value: 'custom', label: 'مخصّص' },
];
